// Module: vault-store
// Contract: ./contract.md   Spec: docs/vault-spec-v2.md §7.8, §10
//
// Holds the decrypted vault document in memory. No crypto, no network, no DOM: this module must
// run unchanged under Node (spec §9.3).

export type Entry = {
  id: string
  title: string
  username: string
  password: string
  url: string
  notes: string
  tags: string[]
  createdAt: string
  updatedAt: string
}

export type EntryInput = Omit<Entry, "id" | "createdAt" | "updatedAt"> & { id?: string }

export type SortField = "title" | "username" | "url" | "updatedAt" | "createdAt"

export type ImportResult = { added: number; updated: number; skipped: number; errors: string[] }

export type StoreConfig = {
  now?: () => string
  uuid?: () => string
}

export type StoreErrorCode = "MalformedDocument" | "UnsupportedSchema" | "InvalidEntry"

export class StoreError extends Error {
  readonly code: StoreErrorCode
  constructor(code: StoreErrorCode, message?: string) {
    super(message ?? code)
    this.name = "StoreError"
    this.code = code
  }
}

export const SCHEMA_VERSION = 1

/** Searched by `list({ query })`. `password` is absent by design: match counts would leak it. */
const SEARCHABLE = ["title", "username", "url", "notes"] as const

// Alphabetical, so canonical output is stable regardless of construction order.
const ENTRY_KEYS = [
  "createdAt",
  "id",
  "notes",
  "password",
  "tags",
  "title",
  "updatedAt",
  "url",
  "username",
] as const

const CSV_COLUMNS = [
  "id",
  "title",
  "username",
  "password",
  "url",
  "notes",
  "tags",
  "createdAt",
  "updatedAt",
] as const

function normalizeTags(tags: unknown): string[] {
  if (!Array.isArray(tags)) return []
  const seen = new Set<string>()
  for (const raw of tags) {
    if (typeof raw !== "string") continue
    const tag = raw.trim().toLowerCase()
    if (tag.length > 0) seen.add(tag)
  }
  return [...seen]
}

function isEntryShape(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null) return false
  const e = value as Record<string, unknown>
  return (
    typeof e["id"] === "string" &&
    typeof e["title"] === "string" &&
    typeof e["username"] === "string" &&
    typeof e["password"] === "string" &&
    typeof e["url"] === "string" &&
    typeof e["notes"] === "string" &&
    Array.isArray(e["tags"]) &&
    typeof e["createdAt"] === "string" &&
    typeof e["updatedAt"] === "string"
  )
}

function toEntry(raw: Record<string, unknown>): Entry {
  return {
    id: raw["id"] as string,
    title: raw["title"] as string,
    username: raw["username"] as string,
    password: raw["password"] as string,
    url: raw["url"] as string,
    notes: raw["notes"] as string,
    tags: normalizeTags(raw["tags"]),
    createdAt: raw["createdAt"] as string,
    updatedAt: raw["updatedAt"] as string,
  }
}

/**
 * Canonical JSON: entries ordered by id, keys in a fixed alphabetical order, no insignificant
 * whitespace. An unchanged document must serialise to identical bytes so `session` can skip a
 * pointless re-encrypt and upload (spec §6.5).
 */
function canonicalJson(entries: Entry[]): string {
  const ordered = [...entries].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  const parts = ordered.map((entry) => {
    const fields = ENTRY_KEYS.map((key) => {
      const value = key === "tags" ? [...entry.tags].sort() : entry[key]
      return `${JSON.stringify(key)}:${JSON.stringify(value)}`
    })
    return `{${fields.join(",")}}`
  })
  return `{"entries":[${parts.join(",")}],"schemaVersion":${SCHEMA_VERSION}}`
}

// --- CSV, RFC 4180 -----------------------------------------------------------------------

function csvEscape(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

function csvParse(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ""
  let quoted = false
  let i = 0

  while (i < text.length) {
    const ch = text[i]!
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i += 2
          continue
        }
        quoted = false
        i += 1
        continue
      }
      field += ch
      i += 1
      continue
    }
    if (ch === '"' && field.length === 0) {
      quoted = true
      i += 1
      continue
    }
    if (ch === ",") {
      row.push(field)
      field = ""
      i += 1
      continue
    }
    if (ch === "\r" || ch === "\n") {
      row.push(field)
      rows.push(row)
      row = []
      field = ""
      i += ch === "\r" && text[i + 1] === "\n" ? 2 : 1
      continue
    }
    field += ch
    i += 1
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field)
    rows.push(row)
  }
  return rows
}

// --- store -------------------------------------------------------------------------------

export interface VaultStore {
  toBytes(): Uint8Array
  list(opts?: { query?: string; sort?: SortField; direction?: "asc" | "desc" }): Entry[]
  get(id: string): Entry | undefined
  count(): number
  upsert(entry: EntryInput): Entry
  remove(id: string): boolean
  exportPlaintext(format: "json" | "csv"): string
  importPlaintext(text: string, format: "json" | "csv", mode: "merge" | "replace"): ImportResult
  readonly dirty: boolean
  markClean(): void
}

class Store implements VaultStore {
  #entries: Entry[]
  #dirty = false
  readonly #now: () => string
  readonly #uuid: () => string

  constructor(entries: Entry[], config: StoreConfig) {
    this.#entries = entries
    this.#now = config.now ?? (() => new Date().toISOString())
    this.#uuid = config.uuid ?? (() => crypto.randomUUID())
  }

  get dirty(): boolean {
    return this.#dirty
  }

  markClean(): void {
    this.#dirty = false
  }

  toBytes(): Uint8Array {
    return new TextEncoder().encode(canonicalJson(this.#entries))
  }

  count(): number {
    return this.#entries.length
  }

  get(id: string): Entry | undefined {
    const found = this.#entries.find((e) => e.id === id)
    return found ? { ...found, tags: [...found.tags] } : undefined
  }

  list(opts: { query?: string; sort?: SortField; direction?: "asc" | "desc" } = {}): Entry[] {
    const { query, sort = "title", direction = "asc" } = opts

    let result = this.#entries
    if (query && query.trim().length > 0) {
      const needle = query.trim().toLowerCase()
      result = result.filter((entry) => {
        for (const field of SEARCHABLE) {
          if (entry[field].toLowerCase().includes(needle)) return true
        }
        return entry.tags.some((tag) => tag.includes(needle))
      })
    }

    const sign = direction === "desc" ? -1 : 1
    // Sort a copy; Array.prototype.sort is stable in every engine we target.
    const sorted = [...result].sort((a, b) => sign * a[sort].localeCompare(b[sort]))
    return sorted.map((entry) => ({ ...entry, tags: [...entry.tags] }))
  }

  upsert(input: EntryInput): Entry {
    for (const field of ["title", "username", "password", "url", "notes"] as const) {
      if (typeof input[field] !== "string") {
        throw new StoreError("InvalidEntry", `field "${field}" must be a string`)
      }
    }
    if (input.tags !== undefined && !Array.isArray(input.tags)) {
      throw new StoreError("InvalidEntry", 'field "tags" must be an array')
    }

    const now = this.#now()
    const existingIndex = input.id ? this.#entries.findIndex((e) => e.id === input.id) : -1

    if (existingIndex >= 0) {
      const existing = this.#entries[existingIndex]!
      const updated: Entry = {
        ...existing,
        title: input.title,
        username: input.username,
        password: input.password,
        url: input.url,
        notes: input.notes,
        tags: normalizeTags(input.tags),
        updatedAt: now,
      }
      this.#entries[existingIndex] = updated
      this.#dirty = true
      return { ...updated, tags: [...updated.tags] }
    }

    const created: Entry = {
      id: input.id ?? this.#uuid(),
      title: input.title,
      username: input.username,
      password: input.password,
      url: input.url,
      notes: input.notes,
      tags: normalizeTags(input.tags),
      createdAt: now,
      updatedAt: now,
    }
    this.#entries.push(created)
    this.#dirty = true
    return { ...created, tags: [...created.tags] }
  }

  remove(id: string): boolean {
    const index = this.#entries.findIndex((e) => e.id === id)
    if (index < 0) return false
    this.#entries.splice(index, 1)
    this.#dirty = true
    return true
  }

  exportPlaintext(format: "json" | "csv"): string {
    if (format === "json") {
      return JSON.stringify({ schemaVersion: SCHEMA_VERSION, entries: this.#entries }, null, 2)
    }
    const rows = [CSV_COLUMNS.join(",")]
    for (const entry of this.#entries) {
      rows.push(
        CSV_COLUMNS.map((col) =>
          csvEscape(col === "tags" ? entry.tags.join(";") : entry[col]),
        ).join(","),
      )
    }
    return rows.join("\r\n")
  }

  importPlaintext(
    text: string,
    format: "json" | "csv",
    mode: "merge" | "replace",
  ): ImportResult {
    const result: ImportResult = { added: 0, updated: 0, skipped: 0, errors: [] }
    const incoming: EntryInput[] = []

    if (format === "json") {
      let parsed: unknown
      try {
        parsed = JSON.parse(text)
      } catch (cause) {
        throw new StoreError("MalformedDocument", `import is not valid JSON: ${String(cause)}`)
      }
      const entries = (parsed as { entries?: unknown })?.entries
      if (!Array.isArray(entries)) {
        throw new StoreError("MalformedDocument", "import has no entries array")
      }
      for (const [i, raw] of entries.entries()) {
        if (typeof raw !== "object" || raw === null) {
          result.skipped += 1
          result.errors.push(`row ${i}: not an object`)
          continue
        }
        const r = raw as Record<string, unknown>
        if (typeof r["title"] !== "string") {
          result.skipped += 1
          result.errors.push(`row ${i}: missing title`)
          continue
        }
        incoming.push({
          id: typeof r["id"] === "string" ? r["id"] : undefined,
          title: r["title"],
          username: typeof r["username"] === "string" ? r["username"] : "",
          password: typeof r["password"] === "string" ? r["password"] : "",
          url: typeof r["url"] === "string" ? r["url"] : "",
          notes: typeof r["notes"] === "string" ? r["notes"] : "",
          tags: normalizeTags(r["tags"]),
        })
      }
    } else {
      const rows = csvParse(text)
      const header = rows.shift()
      if (!header) throw new StoreError("MalformedDocument", "csv import is empty")
      const index = new Map(header.map((h, i) => [h.trim().toLowerCase(), i]))
      if (!index.has("title")) {
        throw new StoreError("MalformedDocument", "csv import has no title column")
      }
      const cell = (row: string[], name: string): string => {
        const i = index.get(name)
        return i === undefined ? "" : (row[i] ?? "")
      }
      for (const [i, row] of rows.entries()) {
        if (row.length === 1 && row[0] === "") continue // trailing newline
        const title = cell(row, "title")
        if (title.length === 0) {
          result.skipped += 1
          result.errors.push(`row ${i + 2}: missing title`)
          continue
        }
        const id = cell(row, "id")
        incoming.push({
          id: id.length > 0 ? id : undefined,
          title,
          username: cell(row, "username"),
          password: cell(row, "password"),
          url: cell(row, "url"),
          notes: cell(row, "notes"),
          tags: cell(row, "tags").split(";"),
        })
      }
    }

    if (mode === "replace") {
      this.#entries = []
      this.#dirty = true
    }

    for (const item of incoming) {
      const matchId =
        item.id ??
        this.#entries.find((e) => e.title === item.title && e.username === item.username)?.id
      const existed = matchId !== undefined && this.#entries.some((e) => e.id === matchId)
      this.upsert(matchId === undefined ? item : { ...item, id: matchId })
      if (existed) result.updated += 1
      else result.added += 1
    }

    return result
  }
}

export function createEmpty(config: StoreConfig = {}): VaultStore {
  return new Store([], config)
}

export function fromBytes(bytes: Uint8Array, config: StoreConfig = {}): VaultStore {
  let parsed: unknown
  try {
    parsed = JSON.parse(new TextDecoder().decode(bytes))
  } catch (cause) {
    throw new StoreError("MalformedDocument", `not valid JSON: ${String(cause)}`)
  }

  if (typeof parsed !== "object" || parsed === null) {
    throw new StoreError("MalformedDocument", "document is not an object")
  }
  const doc = parsed as Record<string, unknown>

  if (doc["schemaVersion"] !== SCHEMA_VERSION) {
    throw new StoreError(
      "UnsupportedSchema",
      `schemaVersion ${String(doc["schemaVersion"])}, expected ${SCHEMA_VERSION}`,
    )
  }
  if (!Array.isArray(doc["entries"])) {
    throw new StoreError("MalformedDocument", "entries is not an array")
  }

  const entries: Entry[] = []
  for (const raw of doc["entries"]) {
    if (!isEntryShape(raw)) throw new StoreError("MalformedDocument", "entry has the wrong shape")
    entries.push(toEntry(raw))
  }

  return new Store(entries, config)
}

export const __testing = { canonicalJson, csvParse, csvEscape, SEARCHABLE }
